"use client";

import { Search } from "lucide-react";
import type { Ref } from "react";

import { FetchInput } from "@/fetch-ui/FetchInput";

interface FetchSwitcherSearchFieldProps {
  /** Current search text. */
  value: string;
  /** Called with the new search text on every keystroke. */
  onChange: (value: string) => void;
  /** e.g. "Find organization..." */
  placeholder: string;
  /** Caller's own focus-ring-stripping classes — kept caller-owned since the two existing
   *  callers each needed slightly different specificity to actually kill the ring. */
  inputClassName: string;
  /** Forwarded to the underlying input, for callers that autofocus it on open. */
  inputRef?: Ref<HTMLInputElement>;
  /**
   * ARIA combobox wiring — for a caller pairing this field with a `role="listbox"` results
   * panel (e.g. arrow-key/highlight navigation over the filtered rows). Omit for a caller with
   * no such pairing (a plain filter box with no keyboard-navigable result list).
   */
  comboboxProps?: {
    "aria-controls": string;
    "aria-activedescendant"?: string;
  };
}

/**
 * The search row shared by every switcher popover (tool, org, all-orgs): a bordered strip
 * holding a `FetchInput` with a leading search glyph. Layout only — search filtering stays with
 * each caller, since what's searchable (tool names vs org names) differs.
 *
 * @param props - See {@link FetchSwitcherSearchFieldProps}.
 * @returns The search field row.
 */
export function FetchSwitcherSearchField({
  value,
  onChange,
  placeholder,
  inputClassName,
  inputRef,
  comboboxProps,
}: FetchSwitcherSearchFieldProps): React.ReactElement {
  return (
    <div className="border-b border-gray-100 px-2">
      <FetchInput
        ref={inputRef}
        placeholder={placeholder}
        aria-label={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        leftIcon={<Search className="size-3.5" />}
        className={inputClassName}
        role={comboboxProps ? "combobox" : undefined}
        aria-expanded={comboboxProps ? true : undefined}
        {...comboboxProps}
      />
    </div>
  );
}
